import type { TokenInfo, TokenList } from '@uniswap/token-lists'
import type { Ajv, ValidateFunction } from 'ajv'

enum ValidationSchema {
  LIST = 'list',
  TOKENS = 'tokens',
}

// Robinhood Stock Token names run past the standard's 60 characters
// (e.g. "Vanguard Morningstar Total Stock Market ETF • Robinhood Token").
const MAX_TOKEN_NAME_LENGTH = 120

const validator = new Promise<Ajv>(async (resolve) => {
  const [ajv, { default: standardSchema }] = await Promise.all([
    import('ajv'),
    import('@uniswap/token-lists/src/tokenlist.schema.json'),
  ])
  const schema = withMaxTokenNameLength(standardSchema, MAX_TOKEN_NAME_LENGTH)
  const validator = new ajv.default({ allErrors: true })
    .addSchema(schema, ValidationSchema.LIST)
    // Adds a meta scheme of Pick<TokenList, 'tokens'>
    .addSchema(
      {
        ...schema,
        $id: schema.$id + '#tokens',
        required: ['tokens'],
      },
      ValidationSchema.TOKENS
    )
  resolve(validator)
})

type TokenListSchema = typeof import('@uniswap/token-lists/src/tokenlist.schema.json')

function withMaxTokenNameLength(schema: TokenListSchema, maxLength: number): TokenListSchema {
  const tokenInfo = schema.definitions.TokenInfo
  return {
    ...schema,
    definitions: {
      ...schema.definitions,
      TokenInfo: {
        ...tokenInfo,
        properties: { ...tokenInfo.properties, name: { ...tokenInfo.properties.name, maxLength } },
      },
    },
  }
}

function getValidationErrors(validate: ValidateFunction | undefined): string {
  return (
    validate?.errors?.map((error) => [error.dataPath, error.message].filter(Boolean).join(' ')).join('; ') ??
    'unknown error'
  )
}

/**
 * Validates an array of tokens.
 * @param json the TokenInfo[] to validate
 */
export async function validateTokens(json: TokenInfo[]): Promise<TokenInfo[]> {
  const validate = (await validator).getSchema(ValidationSchema.TOKENS)
  if (validate?.({ tokens: json })) {
    return json
  }
  throw new Error(`Token list failed validation: ${getValidationErrors(validate)}`)
}

/**
 * Validates a token list.
 * @param json the TokenList to validate
 */
export default async function validateTokenList(json: TokenList): Promise<TokenList> {
  const validate = (await validator).getSchema(ValidationSchema.LIST)
  if (validate?.(json)) {
    return json
  }
  throw new Error(`Token list failed validation: ${getValidationErrors(validate)}`)
}
